/** @jsx jsx */
import { jsx } from '@emotion/core';
import { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';

const GOOGLE_MAPS_API_KEY = 'TO-BE-CONFIRMED';

const GooglePlacesAutocomplete = ({
    value,
    onChange,
    onPlaceSelected,
    placeholder,
    css: customCss,
    id,
    name
}) => {
    const inputRef = useRef(null);
    const autocompleteRef = useRef(null);
    const [scriptLoaded, setScriptLoaded] = useState(false);
    const [inputValue, setInputValue] = useState(value);

    // Sync internal state with external value prop
    useEffect(() => {
        setInputValue(value);
    }, [value]);

    // Load Google Maps script
    useEffect(() => {
        if (window.google && window.google.maps && window.google.maps.places) {
            setScriptLoaded(true);
            return;
        }

        const script = document.createElement('script');
        script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places`;
        script.async = true;
        script.defer = true;
        script.onload = () => setScriptLoaded(true);
        document.head.appendChild(script);

        return () => {
            // Cleanup is handled by the browser
        };
    }, []);

    // Initialize autocomplete
    useEffect(() => {
        if (!scriptLoaded || !inputRef.current) return;

        const options = {
            componentRestrictions: { country: 'uk' },
            fields: ['address_components', 'formatted_address', 'name'],
            types: ['address']
        };

        try {
            autocompleteRef.current = new window.google.maps.places.Autocomplete(
                inputRef.current,
                options
            );

            const listener = autocompleteRef.current.addListener('place_changed', () => {
                const place = autocompleteRef.current.getPlace();

                if (!place.address_components) {
                    return;
                }

                // Parse address components
                const addressData = {
                    addressLine1: '',
                    addressLine2: '',
                    townCity: '',
                    postCode: '',
                    country: 'United Kingdom'
                };

                // Extract street number and route for address line 1
                const streetNumber = place.address_components.find(component =>
                    component.types.includes('street_number')
                );
                const route = place.address_components.find(component =>
                    component.types.includes('route')
                );

                if (streetNumber && route) {
                    addressData.addressLine1 = `${streetNumber.long_name} ${route.long_name}`;
                } else if (route) {
                    addressData.addressLine1 = route.long_name;
                } else if (place.name) {
                    addressData.addressLine1 = place.name;
                }

                // Extract sublocality or neighborhood for address line 2
                const sublocality = place.address_components.find(
                    component =>
                        component.types.includes('sublocality') ||
                        component.types.includes('neighborhood')
                );
                if (sublocality) {
                    addressData.addressLine2 = sublocality.long_name;
                }

                // Extract town/city
                const postalTown = place.address_components.find(component =>
                    component.types.includes('postal_town')
                );
                const locality = place.address_components.find(component =>
                    component.types.includes('locality')
                );

                if (postalTown) {
                    addressData.townCity = postalTown.long_name;
                } else if (locality) {
                    addressData.townCity = locality.long_name;
                }

                // Extract postal code
                const postalCode = place.address_components.find(component =>
                    component.types.includes('postal_code')
                );
                if (postalCode) {
                    addressData.postCode = postalCode.long_name;
                }

                // Extract country
                const country = place.address_components.find(component =>
                    component.types.includes('country')
                );
                if (country) {
                    addressData.country = country.long_name;
                }

                onPlaceSelected(addressData);
            });

            return () => {
                if (listener) {
                    window.google.maps.event.removeListener(listener);
                }
            };
        } catch (error) {
            // Silently handle autocomplete initialization errors
        }
    }, [scriptLoaded, onPlaceSelected]);

    const handleInputChange = e => {
        const newValue = e.target.value;
        setInputValue(newValue);
        onChange(e);
    };

    return (
        <input
            ref={inputRef}
            type="text"
            value={inputValue}
            onChange={handleInputChange}
            placeholder={placeholder}
            css={customCss}
            id={id}
            name={name}
            autoComplete="off"
        />
    );
};

GooglePlacesAutocomplete.propTypes = {
    value: PropTypes.string.isRequired,
    onChange: PropTypes.func.isRequired,
    onPlaceSelected: PropTypes.func.isRequired,
    placeholder: PropTypes.string,
    css: PropTypes.object,
    id: PropTypes.string,
    name: PropTypes.string
};

GooglePlacesAutocomplete.defaultProps = {
    placeholder: '',
    css: {},
    id: '',
    name: ''
};

export default GooglePlacesAutocomplete;
